/**
 * Központi SEO / structured data helperek.
 * Bővített JSON-LD sémák: AutoRepair (geo, opening hours, areaServed,
 * sameAs, makesOffer), Service, FAQPage és BreadcrumbList.
 *
 * A scriptobjektumok közvetlenül átadhatók egy TanStack route head() scripts mezőjének.
 */

import type { ServiceContent } from "@/data/services";

export const SITE_URL = "https://lakroszautoszerviz.com";

const SERVICE_AREAS = [
  "Eger",
  "Felsőtárkány",
  "Egerszalók",
  "Andornaktálya",
  "Maklár",
  "Noszvaj",
  "Ostoros",
] as const;

const PROVIDER = {
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  name: "LAKROSZ Autószerviz",
  image: "https://lakroszautoszerviz.com/assets/hero-garage-B43YtIMK.jpg",
  url: `${SITE_URL}/`,
  telephone: "+36304787619",
  email: "kispolgi170@gmail.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Vécseyvölgy utca 99.",
    addressLocality: "Eger",
    postalCode: "3300",
    addressCountry: "HU",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 47.911611,
    longitude: 20.396861,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "17:00",
    },
  ],
  areaServed: ["Eger", "Felsőtárkány", "Egerszalók", "Andornaktálya", "Maklár", "Noszvaj", "Ostoros"],
  sameAs: ["https://www.facebook.com/LAKROSZAUTOSZERVIZ/"],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Általános autószerviz és diagnosztika" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fékjavítás" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Futómű javítás" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Motorfelújítás" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Műszaki vizsga felkészítés" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Gumiszerelés és centrírozás" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Klímaszerviz" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Motorkerékpár szerviz" } },
  ],
} as const;

type Script = { type: "application/ld+json"; children: string };

const json = (data: unknown): Script => ({
  type: "application/ld+json",
  children: JSON.stringify(data),
});

/** Főoldali AutoRepair + WebSite sémák. */
export function buildHomeSchemas(): Script[] {
  return [
    json(PROVIDER),
    json({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Lakrosz Autószerviz",
      url: `${SITE_URL}/`,
      inLanguage: "hu-HU",
    }),
    json({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Lakrosz Autószerviz",
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo.jpg`,
      telephone: "+36 30 478 7619",
      sameAs: ["https://www.facebook.com/LAKROSZAUTOSZERVIZ/"],
    }),
  ];
}

/** Szolgáltatás aloldal sémák: Service + FAQPage (tünetek) + BreadcrumbList. */
export function buildServiceSchemas(content: ServiceContent, url: string): Script[] {
  return [
    json({
      "@context": "https://schema.org",
      "@type": "Service",
      name: content.cardTitle,
      serviceType: content.cardTitle,
      description: content.metaDescription,
      url,
      provider: PROVIDER,
      areaServed: SERVICE_AREAS.map((name) => ({ "@type": "City", name })),
      audience: { "@type": "Audience", audienceType: "Autótulajdonosok Eger és környéke" },
    }),
    json({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
    json({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Főoldal", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Szolgáltatások", item: `${SITE_URL}/#services` },
        { "@type": "ListItem", position: 3, name: content.cardTitle, item: url },
      ],
    }),
  ];
}

/** Blogcikk BreadcrumbList sémája. */
export function buildBlogBreadcrumb(title: string, url: string): Script {
  return json({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Főoldal", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: title, item: url },
    ],
  });
}
